import { ReactComponent as BaultIcon } from 'assets/svg/portfolio/bault.svg'
import { ReactComponent as FarmIcon } from 'assets/svg/portfolio/farm.svg'
import { ReactComponent as IslandIcon } from 'assets/svg/portfolio/island.svg'
import { ReactComponent as PositionIcon } from 'assets/svg/portfolio/position.svg'
import CurrencyLogo from 'components/CurrencyLogo'
import type { ReactNode } from 'react'
import { ApiVaultData, BaultPosition, FarmPosition, IslandPosition } from 'types/portfolio'
import { formatNumber } from 'utils/formatCurrencyAmount'
import { formatDollarAmount } from 'utils/portfolio/formatDollar'

import { PortfolioSubRowLayout } from './PortfolioSubRowLayout'

type PositionType = 'Island' | 'Bault' | 'Farm'

interface PositionSubRowProps {
  type: PositionType
  value: number
  vault: ApiVaultData
  position: IslandPosition | BaultPosition | FarmPosition
  mobileTopContent?: ReactNode
  underlyingBalances?: {
    token0AmountFormatted: string
    token1AmountFormatted: string
    token0Amount: string
    token1Amount: string
  }
  isLast?: boolean
  actions?: ReactNode
  hideFarmRewards?: boolean
}

export function PositionSubRow({
  type,
  value,
  vault,
  position,
  mobileTopContent,
  underlyingBalances,
  actions,
  hideFarmRewards,
}: PositionSubRowProps) {
  const renderUnderlyingBalances = () => {
    if (vault.token1 && vault.token0 && underlyingBalances) {
      const token0Value =
        parseFloat(underlyingBalances?.token0AmountFormatted || '0') * parseFloat(vault.token0.price?.toString() || '0')
      const token1Value =
        parseFloat(underlyingBalances?.token1AmountFormatted || '0') * parseFloat(vault.token1.price?.toString() || '0')

      return (
        <div className="text-right space-y-0.5">
          <div className="text-xs text-kodiak-text-primary flex items-center justify-end gap-1">
            <span>
              {underlyingBalances?.token0AmountFormatted || '0'} {vault.token0.symbol}
            </span>
            <span className="text-kodiak-text-secondary">({formatDollarAmount(token0Value)})</span>
          </div>
          <div className="text-xs text-kodiak-text-primary flex items-center justify-end gap-1">
            <span>
              {underlyingBalances?.token1AmountFormatted || '0'} {vault.token1.symbol}
            </span>
            <span className="text-kodiak-text-secondary">({formatDollarAmount(token1Value)})</span>
          </div>
        </div>
      )
    }
    return <div className="text-kodiak-text-secondary text-right text-xs">—</div>
  }

  const getPositionTypeBadge = () => {
    const badgeConfig: Record<
      string,
      {
        Icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>
        logoURI?: string
        textColor: string
        bgColor: string
        label: string
      }
    > = {
      Bault: {
        Icon: BaultIcon,
        textColor: '#6EE7B7',
        bgColor: '#05966933',
        label: 'Bault',
      },
      'Reward Vault': {
        textColor: '#FCCB5A',
        bgColor: '#FFB82B33',
        label: 'Reward Vault',
      },
      'Kodiak Farm': {
        Icon: FarmIcon,
        textColor: '#9BD4F5',
        bgColor: '#2B6FCA33',
        label: 'Kodiak Farm',
      },
      Island: {
        Icon: IslandIcon,
        textColor: '#69B1F0',
        bgColor: '#3083DC33',
        label: 'Island',
      },
      v2: {
        Icon: PositionIcon,
        textColor: '#A9BBD0',
        bgColor: '#46608133',
        label: 'V2',
      },
      charm: {
        Icon: PositionIcon,
        textColor: '#A78BFA',
        bgColor: '#A78BFA33',
        label: 'Charm',
      },
    }

    let badgeType: string

    if (type === 'Farm') {
      const farmPosition = position as FarmPosition
      const isKodiak = farmPosition.provider === 'kodiak'
      badgeType = isKodiak ? 'Kodiak Farm' : 'Reward Vault'

      if (!isKodiak) {
        const rewardToken = farmPosition.claimableRewards[0]?.token || farmPosition.farm.rewardTokens[0]
        badgeConfig['Reward Vault'].logoURI = rewardToken?.logoUri
      }
    } else if (type === 'Island') {
      badgeType = vault.provider === 'v2' ? 'v2' : vault.provider === 'charm' ? 'charm' : 'Island'
    } else {
      badgeType = type
    }

    const config = badgeConfig[badgeType] || {
      Icon: PositionIcon,
      textColor: '#94a3b8',
      bgColor: '#47556933',
      label: badgeType,
    }

    const { Icon, logoURI, textColor, bgColor, label } = config

    return (
      <span
        className="flex items-center gap-1 px-2 py-1 rounded-xs text-xs font-medium"
        style={{ color: textColor, backgroundColor: bgColor }}
      >
        <div className="w-4 h-4 [&_svg]:w-full [&_svg]:h-full [&_path]:stroke-current">
          {logoURI ? <CurrencyLogo logoURI={logoURI} size="16px" /> : Icon ? <Icon /> : null}
        </div>
        <span>{label}</span>
      </span>
    )
  }

  const getAPR = () => {
    if (type === 'Farm') {
      const farmPos = position as FarmPosition
      return `${farmPos.apr.toFixed(2)}%`
    }
    if (type === 'Bault') {
      const baultPos = position as BaultPosition
      return `${baultPos.bault.apy.toFixed(2)}%`
    }
    if (type === 'Island') {
      if (vault.apr && vault.apr > 0) {
        return `${vault.apr.toFixed(2)}%`
      }
    }
    return '—'
  }

  const formatRewardDisplay = (reward: FarmPosition['claimableRewards'][number]) => {
    const amountFormatted = formatNumber(BigInt(reward.amount || '0'), reward.token.decimals) || '0'
    const usdValue = formatDollarAmount(reward.amountUSD || 0)
    return { amountFormatted, usdValue, symbol: reward.token.symbol }
  }

  const getUnclaimedRewards = (sizeClassName = 'text-sm') => {
    if (type !== 'Farm') {
      return <div className="text-kodiak-text-secondary text-right text-xs">—</div>
    }
    if (hideFarmRewards) {
      return <div className="text-kodiak-text-secondary text-right text-xs">—</div>
    }

    const farmPos = position as FarmPosition
    if (!farmPos.claimableRewards || farmPos.claimableRewards.length === 0) {
      return <div className="text-kodiak-text-secondary text-right text-xs">—</div>
    }

    return (
      <div className={`text-right ${sizeClassName} text-[#6EE7B7] space-y-1`}>
        {farmPos.claimableRewards.map((reward, index) => {
          const display = formatRewardDisplay(reward)
          return (
            <div key={index}>
              <span>
                {display.amountFormatted} {display.symbol}
              </span>{' '}
              <span className="text-[#6EE7B7]">({display.usdValue})</span>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <PortfolioSubRowLayout
      badge={getPositionTypeBadge()}
      mobileTopContent={mobileTopContent}
      showRangeColumn
      balances={renderUnderlyingBalances()}
      aprText={getAPR()}
      valueText={formatDollarAmount(value)}
      rewards={getUnclaimedRewards()}
      actions={
        actions && type === 'Farm' ? (
          <div className="w-full sm:w-auto [&_button]:w-full sm:[&_button]:w-auto [&_button]:px-[10px] [&_button]:py-[6px]">
            {actions}
          </div>
        ) : (
          actions
        )
      }
      mobileRewardsLabel={type === 'Farm' ? 'Unclaimed Rewards' : undefined}
      mobileRewards={type === 'Farm' ? getUnclaimedRewards('text-xs') : undefined}
      showRewardsPlaceholder
      showActionPlaceholder
    />
  )
}
